import { gaEvent } from "@/lib/track";
import { toast as sonnerToast } from "sonner";
// Native/OS share + copy-link helpers with automatic UTM tagging (Step 26,
// External Sharing). Every user-initiated share/copy link carries campaign
// attribution so GA4 can credit shares:
//   utm_source=share
//   utm_medium=mobile|desktop   (device the share happened on)
//   utm_content=native_share|copy_link  (how it was shared)
//   utm_campaign=<content_type>_<content_id>
// This is intentionally separate from the email-campaign auto-UTM and the admin
// manual UTM builder (UtmToolModal) - do not merge those.

function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  return /Mobi|Android|iPhone|iPad|iPod|Windows Phone/i.test(navigator.userAgent || "");
}

// Append share UTM params to any URL without clobbering existing query params.
export function appendShareUtm(url, { channel, campaign }) {
  try {
    const u = new URL(url, typeof window !== "undefined" ? window.location.origin : undefined);
    u.searchParams.set("utm_source", "share");
    u.searchParams.set("utm_medium", isMobileDevice() ? "mobile" : "desktop");
    u.searchParams.set("utm_content", channel);
    if (campaign) u.searchParams.set("utm_campaign", campaign);
    return u.toString();
  } catch {
    // Relative/opaque URL fallback: manual append.
    const params = new URLSearchParams({
      utm_source: "share",
      utm_medium: isMobileDevice() ? "mobile" : "desktop",
      utm_content: channel,
    });
    if (campaign) params.set("utm_campaign", campaign);
    return `${url}${url.includes("?") ? "&" : "?"}${params.toString()}`;
  }
}

// Copy a link to the clipboard with copy_link UTM tagging + toast.
export async function copyShareLink(baseUrl, campaign, toast, successMsg = "Link copied!") {
  const url = appendShareUtm(baseUrl, { channel: "copy_link", campaign });
  try {
    await navigator.clipboard.writeText(url);
    toast && toast.success && toast.success(successMsg);
    return true;
  } catch {
    toast && toast.error && toast.error("Could not copy link");
    return false;
  }
}

// Admin-only share counters. "native" = navigator.share() resolved, which only means a share
// target was picked (the browser cannot confirm a send); "copy" = clipboard fallback succeeded.
function recordShare(content_type, content_id, method) {
  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/share`, {
    method: "POST", headers: { "Content-Type": "application/json" }, keepalive: true,
    body: JSON.stringify({ content_type, content_id: String(content_id), method }),
  }).catch(() => {});
}

// Build the public share URL and share it: native OS sheet on mobile (tagged
// native_share), clipboard fallback otherwise (tagged copy_link).
export async function shareContent(type, id, title, toast) {
  const base = `${window.location.origin}/s/${type}/${id}`;
  const campaign = `${type}_${id}`;
  const nativeUrl = appendShareUtm(base, { channel: "native_share", campaign });
  const shareData = { title: title ? `${title} - SAMTWO` : "SAMTWO", text: "Check this out on SAMTWO", url: nativeUrl };
  try {
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      await navigator.share(shareData);
      recordShare(type, id, "native");
      gaEvent("share", { method: "native", content_type: type, item_id: String(id) });
      return;
    }
  } catch (e) {
    if (e && e.name === "AbortError") return; // user dismissed the sheet
  }
  // Fallback path is a copy, so tag it as copy_link.
  if (await copyShareLink(base, campaign, toast)) {
    recordShare(type, id, "copy");
    gaEvent("share", { method: "copy", content_type: type, item_id: String(id) });
  }
}

// Share a page URL (blog post, smart link, deep link): native share sheet when the
// device has one, otherwise copy with a "Link copied!" toast. Dismissing the sheet does nothing.
export async function sharePageLink(baseUrl, title, campaign) {
  const shareData = { title: title ? `${title} - SAMTWO` : "SAMTWO", url: appendShareUtm(baseUrl, { channel: "native_share", campaign }) };
  try {
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      await navigator.share(shareData);
      gaEvent("share", { method: "native", content_type: campaign.split("_")[0], item_id: campaign });
      return;
    }
  } catch (e) {
    if (e && e.name === "AbortError") return;
  }
  if (await copyShareLink(baseUrl, campaign, sonnerToast)) {
    gaEvent("share", { method: "copy", content_type: campaign.split("_")[0], item_id: campaign });
  }
}
